import { getRowGroupingFieldFromGroupingCriteria } from "@mui/x-data-grid-premium";
import type { FieldConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { GroupingField } from "../../../../../../../../api/types/svTypes.ts";

export const MIN_COLUMN_WIDTH = 150;
export const MAX_COLUMN_WIDTH = 500;

type ColumnWidthField = Pick<FieldConfiguration, "guid" | "customizations">;

export const getTabularColumnWidthFields = (
  fields: readonly ColumnWidthField[],
  groups: readonly GroupingField[]
): ColumnWidthField[] => {
  const groupFields = groups.map((group) => ({
    guid: getRowGroupingFieldFromGroupingCriteria(group.id),
    customizations: group.customizations,
  }));
  const fixedWidths = Object.values(getTabularColumnWidths(groupFields));
  const combinedGroup: ColumnWidthField = {
    guid: getRowGroupingFieldFromGroupingCriteria(null),
    customizations: {
      autosize: fixedWidths.length === 0,
      columnWidth: fixedWidths.length > 0 ? Math.max(...fixedWidths) : undefined,
    },
  };
  return groups.length > 0 ? [...fields, ...groupFields, combinedGroup] : [...fields];
};

export const getTabularColumnWidths = (fields: readonly ColumnWidthField[]): Record<string, number> => {
  const widths: Record<string, number> = {};
  for (const { guid, customizations } of fields) {
    const columnWidth = customizations?.columnWidth;
    if (
      customizations?.autosize !== true &&
      columnWidth !== undefined &&
      columnWidth !== null &&
      Number.isInteger(columnWidth) &&
      columnWidth > 0
    ) {
      widths[guid] = columnWidth;
    }
  }
  return widths;
};

export interface TabularColumnWidthsState {
  savedWidths: Record<string, number> | undefined;
  widths: ReadonlyMap<string, number>;
  customWidths: ReadonlyMap<string, number>;
}

export const reconcileTabularColumnWidths = (
  previous: TabularColumnWidthsState | undefined,
  savedWidths: Record<string, number> | undefined
): TabularColumnWidthsState => {
  if (previous && previous.savedWidths === savedWidths) {
    return previous;
  }
  const widths = new Map(previous?.widths);
  const customWidths = new Map(previous?.customWidths);
  for (const field of Object.keys(previous?.savedWidths ?? {})) {
    if (savedWidths?.[field] === undefined) {
      widths.delete(field);
      customWidths.delete(field);
    }
  }
  for (const [field, width] of Object.entries(savedWidths ?? {})) {
    if (previous?.savedWidths?.[field] !== width) {
      widths.set(field, width);
      customWidths.set(field, width);
    }
  }
  return { savedWidths, widths, customWidths };
};

export const updateTabularColumnWidth = (
  state: TabularColumnWidthsState,
  field: string,
  width: number | undefined,
  manual: boolean
): TabularColumnWidthsState => {
  if (width === undefined) {
    if (!state.widths.has(field) && !state.customWidths.has(field)) {
      return state;
    }
    const widths = new Map(state.widths);
    const customWidths = new Map(state.customWidths);
    widths.delete(field);
    customWidths.delete(field);
    return { ...state, widths, customWidths };
  }
  if (state.widths.get(field) === width && (!manual || state.customWidths.get(field) === width)) {
    return state;
  }
  return {
    ...state,
    widths: new Map(state.widths).set(field, width),
    customWidths: manual ? new Map(state.customWidths).set(field, width) : state.customWidths,
  };
};

export const isValidColumnWidth = (value: string): boolean => {
  const width = Number(value);
  return value.trim() !== "" && Number.isInteger(width) && width >= MIN_COLUMN_WIDTH && width <= MAX_COLUMN_WIDTH;
};
